import { api } from "@/lib/http.ts";
import { reverseGeocode } from "@/lib/googleApi.ts";
import { getCurrentLocation } from "@/lib/places.ts";
import { createLocationCache } from "@/shared/locationCache.ts";
import type { GetAccountContextResp, UpdateAccountTimezoneReq } from "@/types/accounts.ts";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { isSharedThreadPath } from "@/lib/sharedThread.ts";
import { usePreferencesRequest } from "@/components/preferences/usePreferences.tsx";

/**
 * The browser's address for a message send. Sends read it and never await a
 * lookup: while the location prompt is open, `getCurrentPosition` neither
 * resolves nor times out, and Safari's grant lasts only a day, so awaiting it
 * held the agent until the user answered. The prompt still appears, from
 * `warm`.
 */
const deviceLocation = createLocationCache(async () => {
  try {
    const current = await getCurrentLocation();
    if (!current.location) return null;
    const { latitude, longitude } = current.location;
    return await reverseGeocode(latitude, longitude);
  } catch (error) {
    console.warn("Browser location access failed:", error);
    return null;
  }
});

export const useUserShow = (userId: null | string) => {
  const { data, isLoading } = useQuery<GetAccountContextResp>({
    queryKey: ["user", userId],
    queryFn: () =>
      api.get<GetAccountContextResp>(`/api/a/agents/${userId}`).then((res) => res.data),
    enabled: !!userId,
  });

  return {
    isLoading,
    user: data,
  };
};

/**
 * The signed-in account. A factory so a consumer that only needs the cached
 * value can subscribe to it alone: calling {@link useCurrentUserContext}
 * instead would also mount the preferences query and its persistence effect.
 */
export const currentUserQuery = () =>
  queryOptions({
    queryKey: ["currentUser"] as const,
    queryFn: ({ signal }) =>
      api.get<GetAccountContextResp>("/api/a/users/me", { signal }).then((res) => res.data),
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Skip fetching for shared thread views (unauthenticated users).
    // Non-hook version since this runs before Router is mounted.
    enabled: !isSharedThreadPath(),
  });

export const useCurrentUserContext = () => {
  const queryClient = useQueryClient();
  const { data: preferencesData } = usePreferencesRequest();

  const { data, isLoading, isPending, fetchStatus, error } = useQuery(currentUserQuery());

  const { mutate: updateUserTimezone } = useMutation({
    mutationFn: async (timezone: UpdateAccountTimezoneReq) => {
      await api.post("/api/a/users/me/timezone", timezone);
    },
    onSuccess: (_, variables) => {
      // Update the cache with the new timezone
      queryClient.setQueryData<GetAccountContextResp>(["currentUser"], (currentData) => {
        if (!currentData) return currentData;
        return {
          ...currentData,
          user: {
            ...currentData.user,
            userContact: {
              ...currentData.user.userContact,
              currentTimezone: variables.timeZone,
            },
          },
        };
      });
    },
  });

  const getUserTimezone = useCallback(() => {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (data && data.user.userContact.currentTimezone !== browserTimezone) {
      updateUserTimezone({ timeZone: browserTimezone });
    }
    // Always return the current browser timezone, not the stale cached value
    return browserTimezone;
  }, [data, updateUserTimezone]);

  /**
   * The location to send with a message. Synchronous so no send waits on the
   * browser: it reads a recently resolved device address and refreshes it in
   * the background. With none, the server falls back to the stored addresses.
   */
  const getUserLocation = useCallback((): string | null => {
    const locations = data?.user.userContact.locations;
    const locationPreference = preferencesData?.preferences?.location?.timezonePreference;

    if (locationPreference === "home") {
      const homeLocation = locations?.find((l) => l.type === "home")?.value;
      if (homeLocation) {
        return homeLocation;
      }
    }

    deviceLocation.warm();
    return deviceLocation.getCached() ?? locations?.find((l) => l.type === "other")?.value ?? null;
  }, [data, preferencesData]);

  return {
    error,
    fetchStatus,
    isLoading,
    isPending,
    mutate: () => queryClient.invalidateQueries({ queryKey: ["currentUser"] }),
    userContext: data,
    getUserTimezone,
    getUserLocation,
  };
};
